import * as Yup from 'yup';

import { JobFormData } from '../application/types/JobTypes';
import { isValid, parse } from 'date-fns';

export const JobValidationSchema = Yup.object<JobFormData>({
  id: Yup.number().strict(true),

  // Customer Information
  customerId: Yup.number().strict(true).nullable().required('Customer is required'),
  callerId: Yup.number().strict(true).nullable(),
  customerPhone: Yup.string(),
  customerEmail: Yup.string().email().max(255),

  // Job Information
  jobName: Yup.string(),
  jobSiteId: Yup.number().strict(true).nullable().required('Job site is required'),
  jobTypeId: Yup.number().strict(true),
  jobSiteSizeId: Yup.number().strict(true),
  sizeRequestedId: Yup.number().strict(true).nullable().required('Size requested is required'),
  equipmentYardId: Yup.number().strict(true),
  dispatchLocationId: Yup.number().strict(true).nullable(),
  jobInformationId: Yup.number().strict(true).nullable(),
  jobStatusId: Yup.number().strict(true),
  typeId: Yup.number().strict(true).nullable().required('Job type is required'),
  cod: Yup.boolean(),

  // Order and Dispatch Information
  dispatcherId: Yup.number().strict(true).nullable(),
  salesAgentId: Yup.number().strict(true).nullable(),
  salesAgentName: Yup.string(),
  poNumber: Yup.string(),

  // Date and Time Information
  startDate: Yup.date().typeError('Please enter a valid date.').required('Job Start date is required'),
  startTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true; // Allow empty values
      if (typeof value === 'string') return false; // Reject string values
      if (!(value instanceof Date)) return false; // Must be a Date object
      return !isNaN(value.getTime()); // Must be a valid date
    })
    .test('time-sequence', function (value) {
      const { parent } = this;
      const time1 = value;
      const time2 = parent['onJobTime'];

      if (time1 === null || time1 === undefined || time1 === '' || time2 === null || time2 === undefined || time2 === '') return true;
      if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
      if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

      const invalid = time1.getTime() < time2.getTime();
      if (invalid) {
        return this.createError({
          message: 'Start Time cannot be before On Job Time'
        });
      }
      return true;
    }),
  onJobTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true; // Allow empty values

      // Check if value is a Date object first
      if (value instanceof Date) {
        return !isNaN(value.getTime()); // Must be a valid date
      }

      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const validTime = parse(value, 'HH:mm:ss', new Date());
        return isValid(validTime);
      }

      // Reject any other type
      return false;
    })
    .test('time-sequence', function (value) {
      const { parent } = this;
      const time1 = value;
      const time2 = parent['leaveTime'];

      if (time1 === null || time1 === undefined || time1 === '' || time2 === null || time2 === undefined || time2 === '') return true;
      if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
      if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

      const invalid = time1.getTime() < time2.getTime();
      if (invalid) {
        return this.createError({
          message: 'On Job Time cannot be before Leave Time'
        });
      }
      return true;
    }),
  leaveTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true; // Allow empty values

      // Check if value is a Date object first
      if (value instanceof Date) {
        return !isNaN(value.getTime()); // Must be a valid date
      }

      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const validTime = parse(value, 'HH:mm:ss', new Date());
        return isValid(validTime);
      }

      // Reject any other type
      return false;
    })
    .test('time-sequence', function (value) {
      const { parent } = this;
      const time1 = value;
      const time2 = parent['showUpTime'];

      if (time1 === null || time1 === undefined || time1 === '' || time2 === null || time2 === undefined || time2 === '') return true;
      if (!(time1 instanceof Date) || !(time2 instanceof Date)) return true;
      if (isNaN(time1.getTime()) || isNaN(time2.getTime())) return true;

      const invalid = time1.getTime() < time2.getTime();
      if (invalid) {
        return this.createError({
          message: 'Leave Time cannot be before Show Up Time'
        });
      }
      return true;
    }),
  showUpTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true; // Allow empty values
      if (typeof value === 'string') return false; // Reject string values
      if (!(value instanceof Date)) return false; // Must be a Date object
      return !isNaN(value.getTime()); // Must be a valid date
    }),
  estimatedHours: Yup.number().strict(true).positive('Estimated hours must be a positive number').nullable(),
  timeZone: Yup.string().nullable(),

  // notes
  notes: Yup.string().max(255, 'Notes must be at most 255 characters')
});

export const JobTimelineValidationSchema = Yup.object().shape({
  id: Yup.number().strict(true).required('Job ID is required'),
  startDate: Yup.date().required('Start date is required'),
  startTime: Yup.date().typeError('Please enter a valid time.').required('Start time is required'),
  onJobTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true;
      if (typeof value === 'string') return false;
      if (!(value instanceof Date)) return false;
      return !isNaN(value.getTime());
    }),
  leaveTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true;
      if (typeof value === 'string') {
        const validTime = parse(value, 'HH:mm:ss', new Date());
        return isValid(validTime);
      }
      if (value instanceof Date) {
        return !isNaN(value.getTime());
      }
      return false;
    }),
  showUpTime: Yup.mixed()
    .nullable()
    .test('is-valid-time', 'Please enter a valid time.', function (value) {
      if (value === null || value === undefined || value === '') return true;
      if (typeof value === 'string') return false;
      if (!(value instanceof Date)) return false;
      return !isNaN(value.getTime());
    }),
  estimatedHours: Yup.number().strict(true).positive('Estimated hours must be a positive number').required('Estimated hours is required'),
  timeZone: Yup.string().nullable()
});
